import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { 
  Film, Clock, CheckCircle, Banknote, Play, FileText,
  Mail, Upload, File, Trash2, Download, Loader2,
  ChevronRight, Eye, FolderOpen, AlertCircle, Link as LinkIcon, FileCheck, CreditCard
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const statusSteps = [
  { id: 'soumission', label: 'Soumission', icon: FileText, description: 'Votre projet a été soumis' },
  { id: 'evaluation', label: 'Évaluation', icon: Clock, description: 'Notre équipe étudie votre projet' },
  { id: 'levee', label: 'En cours de levée', icon: Banknote, description: 'Recherche de financement en cours' },
  { id: 'production', label: 'Production', icon: Film, description: 'Votre projet est en production' },
  { id: 'diffusion', label: 'Diffusion', icon: Play, description: 'Votre film est diffusé' },
];

// Documents requis par étape
const requiredDocuments = {
  soumission: ['Synopsis détaillé', 'Note d\'intention', 'CV réalisateur'],
  evaluation: ['Scénario complet', 'Budget prévisionnel', 'Plan de financement'],
  levee: ['Dossier de production', 'Contrats préliminaires'],
  production: ['Planning de tournage', 'Liste technique'],
  diffusion: ['DCP / Fichier de diffusion', 'Matériel promotionnel'],
};

const ProducerDashboard = () => {
  const { token } = useParams();
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [uploadingRib, setUploadingRib] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [adminDocuments, setAdminDocuments] = useState([]);
  const [ribDocument, setRibDocument] = useState(null);

  const fetchProjectData = async () => {
    try {
      setIsLoading(true);
      // Fetch project by token
      const projectRes = await axios.get(`${BACKEND_URL}/api/projet/${token}`);
      
      if (projectRes.data.error) {
        setError(projectRes.data.error);
        setIsLoading(false);
        return;
      }
      
      setProject(projectRes.data);
      
      // Fetch documents for this project
      if (projectRes.data.id) {
        const docsRes = await axios.get(`${BACKEND_URL}/api/documents/${projectRes.data.id}`);
        
        // Find RIB document
        const rib = docsRes.data.find(doc => doc.documentType === 'RIB');
        if (rib) {
          setRibDocument({
            id: rib.id,
            name: rib.fileName,
            type: rib.documentType,
            date: rib.uploadedAt ? new Date(rib.uploadedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A',
            size: rib.fileSize
          });
        } else {
          setRibDocument(null);
        }
        
        // Separate producer and admin documents (excluding RIB)
        const producerDocs = docsRes.data
          .filter(doc => doc.uploadedBy !== 'admin' && doc.documentType !== 'RIB')
          .map(doc => ({
            id: doc.id,
            name: doc.fileName,
            type: doc.documentType,
            date: doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A',
            size: doc.fileSize
          }));
        
        const adminDocs = docsRes.data
          .filter(doc => doc.uploadedBy === 'admin')
          .map(doc => ({
            id: doc.id,
            name: doc.fileName,
            type: doc.documentType,
            date: doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A',
            size: doc.fileSize
          }));
        
        setDocuments(producerDocs);
        setAdminDocuments(adminDocs);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError('Impossible de charger les données du projet');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchProjectData();
    }
  }, [token]);

  const handleFileUpload = async (docType, file) => {
    if (!project || !file) return;
    
    setUploadingDoc(docType);
    try {
      // Upload actual file to backend
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', project.id);
      formData.append('documentType', docType);
      formData.append('uploadedBy', 'producer');
      
      await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Refresh documents list
      await fetchProjectData();
    } catch (err) {
      console.error('Error uploading document:', err);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleRibUpload = async (file) => {
    if (!project || !file) return;
    
    setUploadingRib(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', project.id);
      formData.append('documentType', 'RIB');
      formData.append('uploadedBy', 'producer');
      
      await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      await fetchProjectData();
    } catch (err) {
      console.error('Error uploading RIB:', err);
    } finally {
      setUploadingRib(false);
    }
  };

  const handleDownload = (docId, fileName) => {
    window.open(`${BACKEND_URL}/api/documents/download/${docId}`, '_blank');
  };

  const getStatusIndex = (status) => {
    const index = statusSteps.findIndex(s => s.id === status);
    return index >= 0 ? index : 0;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 size={48} className="text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Header />
        <main className="pt-32 pb-20 flex items-center justify-center min-h-[80vh]">
          <div className="glass rounded-3xl p-10 max-w-md text-center">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-medium text-white mb-2">Lien invalide</h1>
            <p className="text-[#71717a]">Ce lien d'accès n'est pas valide ou a expiré.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const currentStatusIndex = getStatusIndex(project.status);
  const currentStatus = statusSteps[currentStatusIndex];
  const currentRequiredDocs = requiredDocuments[project.status] || [];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="producer-dashboard">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="container-custom">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <span className="overline mb-2 block">Espace Projet</span>
            <h1 className="text-3xl md:text-4xl font-medium text-white">
              {project.projectTitle}
            </h1>
            <p className="text-[#71717a] mt-2">{project.category} • {project.name}</p>
          </motion.div>

          {/* Status Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass rounded-2xl p-6 mb-10 border-l-4 border-[#D4AF37]"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  currentStatusIndex >= 3 ? 'bg-green-500/20' : 'bg-[#D4AF37]/20'
                }`}>
                  <currentStatus.icon size={24} className={
                    currentStatusIndex >= 3 ? 'text-green-400' : 'text-[#D4AF37]'
                  } />
                </div>
                <div>
                  <p className="text-white font-medium">Statut actuel : {currentStatus.label}</p>
                  <p className="text-[#71717a] text-sm">{currentStatus.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-[#71717a] text-sm">
                <LinkIcon size={14} />
                <span className="font-mono text-xs">ID: {project.accessToken}</span>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* Timeline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="glass rounded-2xl p-6"
              >
                <h2 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
                  <Clock size={20} className="text-[#D4AF37]" />
                  Progression du projet
                </h2>
                
                <div className="relative">
                  {/* Progress bar */}
                  <div className="absolute top-4 left-4 right-4 h-1 bg-white/10 rounded-full">
                    <div 
                      className="h-full bg-[#D4AF37] rounded-full transition-all duration-500"
                      style={{ width: `${(currentStatusIndex / (statusSteps.length - 1)) * 100}%` }}
                    />
                  </div>
                  
                  <div className="flex justify-between relative z-10">
                    {statusSteps.map((step, i) => {
                      const isActive = i === currentStatusIndex;
                      const isCompleted = i < currentStatusIndex;
                      const StepIcon = step.icon;
                      
                      return (
                        <div key={step.id} className="flex flex-col items-center" style={{ width: '20%' }}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 transition-all ${
                            isActive ? 'bg-[#D4AF37] text-[#050505] ring-4 ring-[#D4AF37]/30' :
                            isCompleted ? 'bg-green-500 text-white' :
                            'bg-white/10 text-[#71717a]'
                          }`}>
                            {isCompleted ? <CheckCircle size={16} /> : <StepIcon size={14} />}
                          </div>
                          <p className={`text-xs text-center ${
                            isActive ? 'text-white font-medium' : 'text-[#71717a]'
                          }`}>
                            {step.label}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>

              {/* Documents Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="glass rounded-2xl p-6"
              >
                <h2 className="text-lg font-medium text-white mb-6 flex items-center gap-2">
                  <FolderOpen size={20} className="text-[#D4AF37]" />
                  Documents du projet
                </h2>

                {/* Documents requis */}
                <div className="mb-6">
                  <p className="text-[#71717a] text-xs uppercase tracking-wider mb-4">
                    Documents requis pour l'étape "{currentStatus.label}"
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {currentRequiredDocs.map((docType) => {
                      const isUploaded = documents.some(d => d.type === docType);
                      const isUploading = uploadingDoc === docType;
                      
                      return (
                        <div 
                          key={docType}
                          className={`p-4 rounded-xl border transition-all ${
                            isUploaded 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-white/5 border-white/10 hover:border-[#D4AF37]/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              {isUploaded ? (
                                <CheckCircle size={18} className="text-green-400" />
                              ) : (
                                <File size={18} className="text-[#71717a]" />
                              )}
                              <span className={`text-sm ${isUploaded ? 'text-green-400' : 'text-white'}`}>
                                {docType}
                              </span>
                            </div>
                            {!isUploaded && (
                              <label className="cursor-pointer">
                                <input 
                                  type="file" 
                                  className="hidden" 
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleFileUpload(docType, file);
                                  }}
                                  disabled={isUploading}
                                />
                                <span className="flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB] text-sm">
                                  {isUploading ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <Upload size={14} />
                                  )}
                                  {isUploading ? 'Envoi...' : 'Déposer'}
                                </span>
                              </label>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Section RIB */}
                <div className="mb-6">
                  <p className="text-[#71717a] text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
                    <CreditCard size={14} className="text-amber-400" />
                    Coordonnées bancaires (RIB)
                  </p>
                  
                  {ribDocument ? (
                    <div className="flex items-center justify-between p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center">
                          <CreditCard size={22} className="text-amber-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{ribDocument.name}</p>
                          <p className="text-amber-400 text-sm">RIB déposé</p>
                          <p className="text-[#71717a] text-xs">{ribDocument.size} • {ribDocument.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleDownload(ribDocument.id, ribDocument.name)}
                          className="p-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 transition-colors"
                          title="Télécharger"
                        >
                          <Download size={16} />
                        </button>
                        <span className="flex items-center gap-1 text-green-400 text-sm">
                          <CheckCircle size={14} />
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border-2 border-dashed border-amber-500/30 rounded-xl hover:border-amber-500/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                            <CreditCard size={18} className="text-amber-400" />
                          </div>
                          <div>
                            <p className="text-white text-sm">Relevé d'Identité Bancaire</p>
                            <p className="text-[#71717a] text-xs">Requis pour recevoir vos paiements</p>
                          </div>
                        </div>
                        <label className="cursor-pointer">
                          <input 
                            type="file" 
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleRibUpload(file);
                            }}
                            disabled={uploadingRib}
                          />
                          <span className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-[#050505] rounded-lg text-sm font-medium hover:bg-amber-400 transition-colors">
                            {uploadingRib ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                Envoi...
                              </>
                            ) : (
                              <>
                                <Upload size={14} />
                                Déposer mon RIB
                              </>
                            )}
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* Documents de Second Souffle (Admin) */}
                {adminDocuments.length > 0 && (
                  <div className="mb-6">
                    <p className="text-[#71717a] text-xs uppercase tracking-wider mb-4 flex items-center gap-2">
                      <FileCheck size={14} className="text-green-400" />
                      Documents de Second Souffle ({adminDocuments.length})
                    </p>
                    <div className="space-y-2">
                      {adminDocuments.map((doc) => (
                        <div 
                          key={doc.id}
                          className="flex items-center justify-between p-4 rounded-xl bg-green-500/10 border border-green-500/30"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                              <FileCheck size={22} className="text-green-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{doc.name}</p>
                              <p className="text-green-400 text-sm">{doc.type}</p>
                              <p className="text-[#71717a] text-xs">{doc.size} • Reçu le {doc.date}</p>
                            </div>
                          </div>
                          <button 
                            onClick={() => handleDownload(doc.id, doc.name)}
                            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-400 text-sm transition-colors"
                          >
                            <Download size={16} />
                            Télécharger
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documents déposés */}
                <div>
                  <p className="text-[#71717a] text-xs uppercase tracking-wider mb-4">
                    Vos documents déposés ({documents.length})
                  </p>
                  {documents.length === 0 ? (
                    <div className="text-center py-8 text-[#71717a]">
                      <Upload size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Aucun document déposé</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div 
                          key={doc.id}
                          className="flex items-center justify-between p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                              <FileText size={18} className="text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="text-white text-sm">{doc.name}</p>
                              <p className="text-[#71717a] text-xs">{doc.type} • {doc.size} • {doc.date}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button 
                              onClick={() => handleDownload(doc.id, doc.name)}
                              className="p-2 text-[#71717a] hover:text-[#D4AF37] transition-colors"
                              title="Télécharger"
                            >
                              <Download size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Zone de dépôt */}
                <div className="mt-6 p-6 border-2 border-dashed border-white/10 rounded-xl text-center hover:border-[#D4AF37]/50 transition-colors">
                  <Upload size={32} className="text-[#71717a] mx-auto mb-3" />
                  <p className="text-[#a1a1aa] text-sm mb-2">
                    Glissez vos fichiers ici ou cliquez pour parcourir
                  </p>
                  <p className="text-[#71717a] text-xs">
                    PDF, DOC, MP4 • Max 50 MB par fichier
                  </p>
                  <label className="btn-secondary inline-block mt-4 cursor-pointer">
                    <input type="file" className="hidden" multiple />
                    Parcourir les fichiers
                  </label>
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Project Info */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="glass rounded-2xl p-6"
              >
                <h3 className="text-lg font-medium text-white mb-4">Informations</h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-[#71717a] text-xs">Soumis le</p>
                    <p className="text-white">{formatDate(project.timestamp)}</p>
                  </div>
                  <div>
                    <p className="text-[#71717a] text-xs">Catégorie</p>
                    <p className="text-white">{project.category}</p>
                  </div>
                  <div>
                    <p className="text-[#71717a] text-xs">Contact</p>
                    <p className="text-white">{project.email}</p>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/10">
                  <a 
                    href="mailto:contact@secondsouffleprod.com"
                    className="btn-primary w-full text-center block"
                  >
                    <Mail size={16} className="inline mr-2" />
                    Contacter l'équipe
                  </a>
                </div>
              </motion.div>

              {/* Share Link */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="glass rounded-2xl p-6"
              >
                <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                  <LinkIcon size={18} className="text-[#D4AF37]" />
                  Lien d'accès
                </h3>
                <p className="text-[#71717a] text-sm mb-4">
                  Gardez ce lien précieusement pour accéder à votre espace projet.
                </p>
                <div className="p-3 rounded-lg bg-white/5 font-mono text-xs text-[#a1a1aa] break-all">
                  {window.location.origin}/projet/{project.accessToken}
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProducerDashboard;
